import { InLifeLayout } from '@in-life/components/Layout';
import { inLifePaths } from '@in-life/paths';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import { ErrorIndicator } from '@components/ErrorIndicator';
import { EnergyProvider, useEnergyContext } from '@features/Energy/Provider';
import { EnergyJourneyPaths } from '@features/Energy/journeyPaths';
import { usePageStateHandler } from '@hooks/usePageStateHandler';
import { withProfiler } from '@services/sentry/withProfiler';
import { stripTrailingSlash } from '@utils/routing/stripTrailingSlash';

const PAGE_TITLE = 'Add energy';

const inLifeEnergyPaths: EnergyJourneyPaths = {
  tariff: inLifePaths.energyTariff,
  details: inLifePaths.energyDetails,
  noEnergyFallback: inLifePaths.energyTariff,
};

const EnergyOutlet = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { basketRaw, isBasketLoading, basketError, isDetailsFormChanged } =
    useEnergyContext();
  const saveChangesDialog = useDialog(DialogID.SAVE_CHANGES_ENERGY);

  const { isLoading, hasError } = usePageStateHandler({
    loading: isBasketLoading,
    hasData: !!basketRaw,
    error: basketError,
  });

  const isDetailsPage =
    stripTrailingSlash(pathname) === inLifePaths.energyDetails;

  const handleBack = isDetailsPage
    ? () => {
        if (isDetailsFormChanged) {
          saveChangesDialog.setOpen(true);
          return;
        }

        navigate(inLifePaths.energyTariff);
      }
    : undefined;

  const renderContent = () => {
    if (isLoading) {
      return null;
    }

    return hasError ? <ErrorIndicator /> : <Outlet />;
  };

  return (
    <InLifeLayout
      title={PAGE_TITLE}
      onBack={handleBack}
      backButtonLabel={isDetailsPage ? 'Back to tariff' : undefined}
    >
      {renderContent()}
    </InLifeLayout>
  );
};

const EnergyProviderUnwrapped = () => (
  <EnergyProvider journeyPaths={inLifeEnergyPaths}>
    <EnergyOutlet />
  </EnergyProvider>
);

const EnergyPage = withProfiler(EnergyProviderUnwrapped);

export { EnergyPage };
